import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { State } from 'types/redux'
import {
  changeMode,
  loadStats,
} from '@disco/state-management/actions/StatsActions'
import { ANALYTICS_UNLIMITED } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { formatTimestamp } from '@disco/utils/time-utils'
import StatsDateRange from 'components/common/stats-date-range'
import DocumentHeadTags from 'components/document-head-tags'
import DataTable from 'components/ui/data-table'
import InfiniteScroll from 'components/ui/infinite-scroll'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'

const InsightsRecipientsPlaylistsViews = (): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    endDate,
    entities: { playlists },
    startDate,
  }: State['stats'] = useSelector(state => state.stats)
  const isFeatureEnabled = useFeatureEnabled(ANALYTICS_UNLIMITED)
  useEffect(() => {
    if (!isFeatureEnabled) {
      navigate('/insights/recipients')
    }
  }, [isFeatureEnabled, navigate])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadData = useCallback(async () => {
    await dispatch(loadStats('playlists', 'views'))
    setIsLoading(false)
  }, [dispatch])
  useEffect(() => {
    dispatch(changeMode('end-user'))
  }, [])
  useEffect(() => {
    if (isFeatureEnabled) {
      setIsLoading(true)
      loadData()
    }
  }, [endDate, isFeatureEnabled, startDate])
  const loadNext = useCallback(
    (next: string) => {
      dispatch(loadStats('playlists', 'views', true))
    },
    [dispatch],
  )
  return (
    <>
      <PageHeader.Breadcrumbs
        breadcrumbs={{
          content: 'Back',
          link: '/insights/recipients',
        }}
      />
      <PageContainer>
        <PageContainer.Section
          title="Most accessed playlists"
          action={<StatsDateRange disabled={!isFeatureEnabled} />}
        >
          <DocumentHeadTags title="Most accessed playlists | DISCO" />
          <InfiniteScroll next={playlists.views.next} loadNext={loadNext}>
            <DataTable
              appearance="plain"
              columns={[
                {
                  name: 'Playlist',
                  render: (item: Record<string, any>) => item.name,
                },
                {
                  name: 'Last updated',
                  render: (item: Record<string, any>) =>
                    formatTimestamp(item.timestamp),
                },
                {
                  name: 'Accessed',
                  width: 'content',
                  render: (item: Record<string, any>) => item.stats.view_count,
                },
              ]}
              data={playlists.views.data}
              loading={isLoading}
              rows={5}
            />
          </InfiniteScroll>
        </PageContainer.Section>
      </PageContainer>
    </>
  )
}

export default InsightsRecipientsPlaylistsViews
